import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { ArrowRight, Play } from 'lucide-react';
import BeforeAfterSlider from './BeforeAfterSlider';

const PAYMENT_URL = process.env.REACT_APP_STRIPE_PAYMENT_URL || 'https://myeaitradingcompass.com/trading-subscription';

export default function Hero({ onOpenModal, stripeUrl }) {
  const handleStartChallenge = () => {
    // Use stripeUrl prop if provided, otherwise fallback to default
    const targetUrl = stripeUrl || PAYMENT_URL;
    window.open(targetUrl, '_blank');
  };

  const handleWatchVideo = () => {
    const videoSection = document.getElementById('video-section');
    if (videoSection) {
      videoSection.scrollIntoView({ behavior: 'smooth' });
      // Try to auto-play the video after scrolling
      setTimeout(() => {
        const video = document.querySelector('[data-video-player]');
        if (video && video.play) {
          video.muted = true;
          video.play().catch(() => {});
        }
      }, 800);
    }
  };

  return (
    <section className="relative isolate overflow-hidden bg-gradient-to-b from-white via-slate-50 to-white">
      {/* Subtle decorative elements */}
      <div className="absolute inset-0 bg-[radial-gradient(600px_400px_at_80%_20%,rgba(37,99,235,0.08),transparent_70%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(400px_300px_at_20%_80%,rgba(37,99,235,0.05),transparent_70%)] pointer-events-none" />
      
      <div className="relative mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8 pt-20 md:pt-28 pb-14 md:pb-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="text-center max-w-4xl mx-auto"
        >
          <h1 
            data-testid="hero-heading"
            className="font-[family-name:Space_Grotesk] text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 leading-tight"
          >
            A Trading Compass Between Your Analysis and Your Execution
          </h1>
          
          <p 
            data-testid="hero-subheading"
            className="mt-6 text-base md:text-lg text-slate-600 max-w-2xl mx-auto"
          >
            Built for traders who know how to analyse but struggle with discipline, risk control, and emotional consistency.
          </p>
          
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={handleStartChallenge}
              data-testid="hero-start-challenge-button"
              className="rounded-xl bg-blue-600 text-white font-semibold px-6 py-3.5 md:px-8 md:py-4 text-base shadow-lg hover:bg-blue-700 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 transition-all duration-200"
            >
              Start 21-Day Challenge
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <button
              onClick={handleWatchVideo}
              data-testid="hero-watch-video-link"
              className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-2 text-base font-medium transition-colors"
            >
              <Play className="h-4 w-4" />
              Watch How It Works
            </button>
          </div>
        </motion.div>

        {/* Before/After Slider - Shows transformation */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-16 md:mt-20"
        >
          <BeforeAfterSlider />
        </motion.div>
      </div>
    </section>
  );
}
